import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import UserDetail from "@/pages/UserDetail";
import Cadence from "@/pages/Cadence";
import Guardrails from "@/pages/Guardrails";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Templates from "@/pages/Templates";
import WhatsAppNumbers from "@/pages/WhatsAppNumbers";
import Digests from "@/pages/Digests";
import AuditLogs from "@/pages/AuditLogs";
import Team from "@/pages/Team";
import AIControl from "@/pages/AIControl";
import Settings from "@/pages/Settings";
import Labels from "@/pages/Labels";
import AIConnectors from "@/pages/AIConnectors";
import MessagingConnectors from "@/pages/MessagingConnectors";
import Statistics from "@/pages/Statistics";
import BehaviorSignals from "@/pages/BehaviorSignals";
import CommunicationLogs from "@/pages/CommunicationLogs";
import Offers from "@/pages/Offers";
import Groups from "@/pages/Groups";
import PromoAnalytics from "@/pages/PromoAnalytics";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import DataDeletionPolicy from "@/pages/DataDeletionPolicy";
import "@/App.css";

function Private({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Public legal pages — accessible without login (required by Meta / app stores) */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/data-deletion-policy" element={<DataDeletionPolicy />} />
            <Route
              path="/"
              element={
                <Private>
                  <Layout />
                </Private>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="users/:id" element={<UserDetail />} />
              <Route path="cadence" element={<Private adminOnly><Cadence /></Private>} />
              <Route path="ai-timings" element={<Navigate to="/settings" replace />} />
              <Route path="ai-connectors" element={<Private adminOnly><AIConnectors /></Private>} />
              <Route path="messaging-connectors" element={<Private adminOnly><MessagingConnectors /></Private>} />
              <Route path="guardrails" element={<Private adminOnly><Guardrails /></Private>} />
              {/* Legacy — redirect stale bookmarks. Live Sales Mode config moved into /guardrails. */}
              <Route path="agentic-phrases" element={<Navigate to="/guardrails" replace />} />
              <Route path="agentic-settings" element={<Navigate to="/guardrails" replace />} />
              <Route path="knowledge-base" element={<Private adminOnly><KnowledgeBase /></Private>} />
              <Route path="templates" element={<Private adminOnly><Templates /></Private>} />
              <Route path="labels" element={<Private adminOnly><Labels /></Private>} />
              <Route path="whatsapp-numbers" element={<Private adminOnly><WhatsAppNumbers /></Private>} />
              <Route path="digests" element={<Digests />} />
              <Route path="statistics" element={<Statistics />} />
              <Route path="audit" element={<AuditLogs />} />
              <Route path="communication-logs" element={<CommunicationLogs />} />
              <Route path="offers" element={<Private adminOnly><Offers /></Private>} />
              <Route path="promo-analytics" element={<Private adminOnly><PromoAnalytics /></Private>} />
              <Route path="groups" element={<Private adminOnly><Groups /></Private>} />
              <Route path="team" element={<Private adminOnly><Team /></Private>} />
              <Route path="ai-control" element={<Private adminOnly><AIControl /></Private>} />
              <Route path="behavior-signals" element={<Private adminOnly><BehaviorSignals /></Private>} />
              <Route path="settings" element={<Private adminOnly><Settings /></Private>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
